import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { NavBar, Toast } from "antd-mobile";
import { wecomApi } from "../wecomApi";
import { TextCoachingChat } from "./TextCoachingChat";
import type {
  CoachingSessionApi,
  SessionActiveResp,
  SessionResultResp,
} from "./coachingSessionApi";
import type { CoachingScenarioItem, CoachingChatResponse } from "./types";
import { errorText } from "@/shared/lib/apiErrors";

interface Props {
  scene: CoachingScenarioItem;
  /** 任务 ID(从 H5 入口 URL 携带)。非空时启用续聊检测。 */
  taskId?: number;
  onBack: () => void;
}

/** 企微 MEMBER 端(wecom-h5.html)的数据面:cookie 身份,走 /wecom-h5/coaching/*。 */
const memberApi: CoachingSessionApi = {
  chatStart: (scenarioId, taskId) =>
    wecomApi.post<CoachingChatResponse>("/wecom-h5/coaching/chat/start", { scenarioId, taskId }),
  chat: (sessionId, message, scenarioId) =>
    wecomApi.post<CoachingChatResponse>("/wecom-h5/coaching/chat", {
      sessionId,
      message,
      scenarioId,
    }),
  result: (sessionId) =>
    wecomApi.get<SessionResultResp>(`/wecom-h5/coaching/sessions/${sessionId}/result`),
  sessionActive: (scenarioId, taskId) =>
    wecomApi.get<SessionActiveResp>(
      taskId
        ? `/wecom-h5/coaching/session/active?taskId=${taskId}`
        : `/wecom-h5/coaching/session/active?scenarioId=${scenarioId}`
    ),
  history: (sessionId) =>
    wecomApi.get<{ turns: { role: string; content: string }[] }>(
      `/wecom-h5/coaching/sessions/${sessionId}/history`
    ),
  finish: (sessionId, scenarioId) =>
    wecomApi.post<CoachingChatResponse>("/wecom-h5/coaching/chat/finish", {
      sessionId,
      scenarioId,
    }),
};

/**
 * 企微 MEMBER 端文本对练页 —— 只剩页头 + 数据面注入,状态机与对话区在 {@link TextCoachingChat}。
 *
 * <p>此前这里和 advisor-h5 的 CoachingChatPage 各写了一份同样的状态机,「修一个 bug 要改两处」。
 */
export function CoachingChat({ scene, taskId, onBack }: Props) {
  const { t } = useTranslation("coaching");
  const queryClient = useQueryClient();

  const refreshHome = () => queryClient.invalidateQueries({ queryKey: ["wecom-my-home"] });

  const handleBack = () => {
    refreshHome();
    onBack();
  };

  const placeholder = useMemo(
    () =>
      scene.mode === "qa_drill"
        ? t("wecomChat.answerPlaceholder")
        : t("wecomChat.inputPlaceholder"),
    [scene.mode, t]
  );

  return (
    <div className="flex flex-col h-full">
      <NavBar onBack={handleBack} className="border-b border-border bg-card shrink-0">
        {scene.title ?? scene.name}
      </NavBar>

      <TextCoachingChat
        scenarioId={scene.id}
        taskId={taskId ?? null}
        api={memberApi}
        tone="light"
        inputPlaceholder={placeholder}
        sendLabel={t("wecomChat.send")}
        backLabel={taskId ? t("wecomChat.backHome") : t("wecomChat.backScenes")}
        onBack={handleBack}
        onEvaluated={refreshHome}
        onStartFailed={(e) => {
          Toast.show({ icon: "fail", content: errorText(e) });
          // 任务路径下被拒(已完成 / 已过期)→ 刷新列表并自动退回主页
          if (taskId) {
            refreshHome();
            setTimeout(onBack, 1200);
          }
        }}
      />
    </div>
  );
}
